import React from 'react';
import { Link } from 'react-router-dom';
import { Anchor, MapPin, Phone, Mail } from 'lucide-react';
import { FaInstagram, FaFacebook, FaTwitter, FaYoutube, FaLinkedin } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

const Footer: React.FC = () => {
  const { t } = useLanguage();

  const socialLinks = [
    { icon: FaInstagram, href: 'https://www.instagram.com/aymerdenizcilik/', color: 'hover:text-pink-500' },
    { icon: FaLinkedin, href: 'https://www.linkedin.com/public-profile/settings?trk=d_flagship3_profile_self_view_public_profile', color: 'hover:text-blue-700' },
    { icon: FaFacebook, href: 'https://www.instagram.com/aymerdenizcilik/', color: 'hover:text-blue-500' },
    { icon: FaTwitter, href: 'https://www.instagram.com/aymerdenizcilik/', color: 'hover:text-blue-400' },
    { icon: FaYoutube, href: 'https://www.instagram.com/aymerdenizcilik/', color: 'hover:text-red-500' }
  ];

  const quickLinks = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('about') },
    { path: '/products', label: t('products') },
    { path: '/media', label: t('media') },
    { path: '/contact', label: t('contact') }
  ];

  return (
    <footer className="bg-slate-800 text-white border-t border-slate-700">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/resimler/logo1.png" 
                alt="footerlogo" 
                className="h-12 w-auto"
              />
              <span className="text-xl font-bold">Aymer Safety</span>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              <Link 
                to="/about" 
                className="hover:text-white transition-colors duration-200 cursor-pointer"
              >
                {t('footerDescription')}
              </Link>
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-slate-400 hover:text-amber-400 transition-colors duration-200`}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-amber-400">{t('quickLinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-300 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-amber-400">{t('contactInfo')}</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-amber-400 mt-1 flex-shrink-0" />
                <Link to="/contact" className="text-slate-300 hover:text-white transition-colors duration-200 text-sm">
                  {t('companyAddress')}
                </Link>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-amber-400" />
                <Link to="/contact" className="text-slate-300 hover:text-white transition-colors duration-200 text-sm">
                  {t('companyPhone')}
                </Link>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-4 w-4 text-amber-400" />
                <Link to="/contact" className="text-slate-300 hover:text-white transition-colors duration-200 text-sm">
                  {t('companyEmail')}
                </Link>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-amber-400">{t('services')}</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>
                <Link to="/products" className="hover:text-white transition-colors duration-200">
                  {t('fireDetectionSystems')}
                </Link>
              </li>
              <li>
                <Link to="/products" className="hover:text-white transition-colors duration-200">
                  {t('fireExtinguishingSystems')}
                </Link>
              </li>
              <li>
                <Link to="/products" className="hover:text-white transition-colors duration-200">
                  {t('emergencyLighting')}
                </Link>
              </li>
              <li>
                <Link to="/products" className="hover:text-white transition-colors duration-200">
                  {t('safetyEquipments')}
                </Link>
              </li>
              <li>
                <Link to="/products" className="hover:text-white transition-colors duration-200">
                  {t('marineElectronics')}
                </Link>
              </li>
              <li>
                <Link to="/products" className="hover:text-white transition-colors duration-200">
                  {t('protectiveClothing')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-400 text-sm">
              © 2025 Aymer Safety. {t('allRightsReserved')}
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/contact" className="text-slate-400 hover:text-white transition-colors duration-200">
                {t('privacyPolicy')}
              </Link>
              <Link to="/contact" className="text-slate-400 hover:text-white transition-colors duration-200">
                {t('termsOfUse')}
              </Link>
              <Link to="/contact" className="text-slate-400 hover:text-white transition-colors duration-200">
                {t('cookiePolicy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;